import * as Sentry from '@sentry/react';
import { scrubPiiFields, scrubUrlParams } from '@/utils/pii-scrubber';

export function initSentry(env: ImportMetaEnv): void {
  const rawSentryDsn = env.VITE_SENTRY_DSN;
  const sentryDsn = rawSentryDsn?.trim();

  if (rawSentryDsn && sentryDsn !== rawSentryDsn) {
    console.warn(
      '[Sentry] VITE_SENTRY_DSN had trailing whitespace/newline (len %d → %d). Vercel env fix still required — see #3420.',
      rawSentryDsn.length,
      sentryDsn?.length
    );
  }

  if (!sentryDsn) {
    console.warn('⚠️ Sentry DSN not configured - monitoring disabled');
    console.warn('Set VITE_SENTRY_DSN in .env.local or Vercel env vars');
    return;
  }

  try {
    Sentry.init({
      dsn: sentryDsn,
      tunnel: 'https://api.ripia.jp/api/v1/sentry-tunnel',
      enabled: true,
      environment: env.MODE,

      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      tracesSampleRate: 1.0,

      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,

      beforeSend(event) {
        if (event.user) {
          delete event.user.email;
          delete event.user.username;
        }

        try {
          if (event.request?.url && typeof event.request.url === 'string') {
            event.request.url = scrubUrlParams(event.request.url);
          }
          if (
            event.request?.query_string &&
            typeof event.request.query_string === 'string'
          ) {
            event.request.query_string = scrubUrlParams(
              '?' + event.request.query_string
            ).slice(1);
          }
          if (event.request?.data) {
            if (typeof event.request.data === 'object') {
              event.request.data = scrubPiiFields(
                event.request.data as Record<string, unknown>
              );
            } else if (typeof event.request.data === 'string') {
              try {
                const parsed = JSON.parse(event.request.data);
                if (parsed && typeof parsed === 'object') {
                  event.request.data = JSON.stringify(scrubPiiFields(parsed));
                }
              } catch {
                // not JSON, leave as-is
              }
            }
          }
        } catch (e) {
          console.warn('[Sentry] PII scrub failed for request:', e);
        }

        try {
          if (event.breadcrumbs) {
            for (const breadcrumb of event.breadcrumbs) {
              if (breadcrumb.data) {
                breadcrumb.data = scrubPiiFields(
                  breadcrumb.data as Record<string, unknown>
                );
                if (typeof breadcrumb.data.url === 'string') {
                  breadcrumb.data.url = scrubUrlParams(breadcrumb.data.url);
                }
              }
            }
          }
        } catch (e) {
          console.warn('[Sentry] PII scrub failed for breadcrumbs:', e);
        }

        try {
          if (event.contexts) {
            for (const [key, ctx] of Object.entries(event.contexts)) {
              if (ctx && typeof ctx === 'object') {
                event.contexts[key] = scrubPiiFields(
                  ctx as Record<string, unknown>
                );
              }
            }
          }
        } catch (e) {
          console.warn('[Sentry] PII scrub failed for contexts:', e);
        }

        if (env.MODE === 'development') {
          console.log('Sentry event (development):', event);
        }
        return event;
      },
    });

    // Issue #3601: post-init verify — Sentry.init は invalid DSN で silent fail
    // (makeDsn returns undefined; client.ts:647-653 does not try/catch).
    // getClient()?.getDsn() === undefined が DSN parse failure の最も精密な signal。
    // getOptions().dsn は input string そのまま返るため検出に使えない (client.ts:686-689)。
    if (Sentry.getClient()?.getDsn() === undefined) {
      console.error(
        '[Sentry] init completed but DSN parse failed (dsn.length=%d). See #3420.',
        sentryDsn.length
      );
    } else {
      console.log('✅ Sentry monitoring enabled:', env.MODE);
    }
  } catch (error) {
    // Sentry.init / makeDsn は通常 throw しないが、 integration 構築失敗等の
    // unexpected throw を catch する belt-and-suspenders。
    console.error('[Sentry] init threw unexpectedly:', error);
  }
}
